import {
  Box2,
  Box3,
  BoxGeometry,
  BufferGeometry,
  Color,
  LineSegments,
  Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Quaternion,
  Sphere,
  SphereGeometry,
  Vector2,
  Vector3,
} from "three";
import _ from "lodash";
import ReactDOM from "react-dom";
import { I18N } from "oc-I18N";
import { v2, v3 } from "oc-threejs/short3";
import * as Commons from "oc-commons/Commons";
import * as Doms from "oc-ui/Doms";
import * as Textures from "oc-threejs/Textures";
import { RefObject, useEffect, useRef, useState } from "react";

export const rectToBox = (r: DOMRect | null): Box2 | null => {
  return r == null ? null : new Box2(v2(r.x, r.y), v2(r.x + r.width, r.y + r.height));
};

export const centerOfElement = (element: HTMLElement): Vector2 =>
  (rectToBox(element.getBoundingClientRect()) as Box2).getCenter(v2());

export const shrinkRectByFactor = (
  boundingClientRect: DOMRect,
  factor: number
): { x: number; y: number; width: number; height: number } => {
  const r = boundingClientRect;
  return {
    x: r.x + r.width * factor,
    y: r.y + r.height * factor,
    width: r.width * (1 - 2 * factor),
    height: r.height * (1 - 2 * factor),
  };
};

export const lineCurveQuaternion = (
  quaternionA: Quaternion,
  quaternionB: Quaternion
): { getPoint(t: number): Quaternion } => {
  const [a, b] = [quaternionA, quaternionB].map((x) => x.clone());
  return {
    getPoint(t: number) {
      const q = new Quaternion();
      q.slerpQuaternions(a, b, t);
      return q;
    },
  };
};

export const point = (
  position: Vector3 = v3(),
  color: number = 0xff0000,
  size: number = 0.02
): Mesh => {
  const geometry = new SphereGeometry(size, 8, 8);
  const material = new MeshBasicMaterial({ color });
  const sphere = new Mesh(geometry, material);
  sphere.position.copy(position);
  return sphere;
};

export const vertexAccessor = (
  geometry: BufferGeometry
): {
  length(): number;
  get(i: number): Vector3;
  set(i: number, vertex: Vector3): void;
  needsUpdate(): void;
} => ({
  length() {
    return geometry.attributes.position.count;
  },
  get(i) {
    const p = geometry.attributes.position.array;
    const j = i * 3;
    return v3(p[j + 0], p[j + 1], p[j + 2]);
  },
  set(i, vertex) {
    const p = geometry.attributes.position.array;
    const j = i * 3;
    p[j + 0] = vertex.x;
    p[j + 1] = vertex.y;
    p[j + 2] = vertex.z;
  },
  needsUpdate() {
    geometry.attributes.position.needsUpdate = true;
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
  },
});

export const line = (points: Vector3[], material: Material): LineSegments =>
  new LineSegments(new BufferGeometry().setFromPoints(points), material);

export const color = (...args) => new Color(...args);

export const gray = (h: number): Color => new Color(h, h, h);

export const colorFromStyle = Commons.cacheIdentity(
  (value) =>
    new Color(getComputedStyle(document.documentElement).getPropertyValue(value).replace(/\s/g, ""))
);

export const setImageAsBackground = (container: HTMLElement, image: string): void => {
  container.style.backgroundImage = `url(${image})`;
  container.style.backgroundPosition = `center`;
  container.style.backgroundSize = `contain`;
  container.style.backgroundRepeat = `no-repeat`;
};

export const boundingBox = (object: Object3D): Box3 => new Box3().setFromObject(object);

export const boundingSphere = (object: Object3D): Sphere =>
  boundingBox(object).getBoundingSphere(new Sphere());

export const reactRenderStatic = (jsx: React.ReactElement): Promise<HTMLElement> => {
  return new Promise((resolve, reject) => {
    const dummy = document.createElement("div");
    ReactDOM.render(jsx, dummy, () => resolve(dummy.children[0] as HTMLElement));
  });
};

export const saveUserFeedback = (Text: string, Stacktrace: string = ""): Promise<any> => {
  if (!APP) {
    return Promise.resolve();
  }
  const feedback = {
    Text,
    Stacktrace,
    KonfigurationCode: APP.configuration?.Code,
    UserAgent: navigator.userAgent,
    UserGuid: APP.settings.userGuid,
    Sprache: I18N.getLanguage(),
    Tenant: APP.settings.tenant_key,
  };
  console.log("feedback", feedback);
  if (APP.settings.doNotWriteUserFeedback) {
    return Promise.resolve();
  }
  return APP.wkfgBackendApi.saveMetadata("UserFeedback", "create", feedback);
};

export const querySelectorAll = (selector: string): HTMLElement[] => {
  const nodeList = document.querySelectorAll(selector);
  const list: HTMLElement[] = [];
  nodeList.forEach((x) => list.push(x as HTMLElement));
  return list;
};

const unitBoxGeometry: BoxGeometry = new BoxGeometry(1, 1, 1);

export const createUnitBoxMesh = (material: Material, name: string): Mesh => {
  const mesh = new Mesh(unitBoxGeometry, material);
  mesh.name = name;
  return mesh;
};

export const minMaxBox2 = (massMeta: {
  MinMaxX?: { x: number; y: number } | null;
  MinMaxY?: { x: number; y: number } | null;
  Standard: { x: number; y: number };
}): Box2 => {
  const box = new Box2();
  box.min.x = massMeta.MinMaxX?.x ?? massMeta.Standard.x;
  box.max.x = massMeta.MinMaxX?.y ?? massMeta.Standard.x;
  box.min.y = massMeta.MinMaxY?.x ?? massMeta.Standard.y;
  box.max.y = massMeta.MinMaxY?.y ?? massMeta.Standard.y;
  return box;
};

export const clampBox2Vector3 = (box2: Box2, vector3: Vector3): Vector3 => {
  const vector2 = box2.clampPoint(vector3 as any, v2());
  vector3.x = vector2.x;
  vector3.y = vector2.y;
  return vector3;
};

export const label3d = (labelString: string, warning: boolean = false): any =>
  Textures.createTextPlane(labelString, {
    orginalHeight: 0.07,
    color: warning ? "red" : "black",
  });

export const formatM = (x: number, digits: number = 3): string =>
  I18N.formatFloat(x, digits) + " m";

export const formatLength = (x: number, digits: number = 3): string => {
  if (Math.abs(x) >= 1) {
    return I18N.formatFloat(x, digits) + " m";
  }
  return I18N.formatFloat(x * 100, Math.max(0, digits - 2)) + " cm";
};

export const useForceUpdateOnResize = (): [unknown, RefObject<HTMLElement | null>] => {
  const watchUpdateRef = useRef<HTMLElement | null>(null);
  const [state, setState] = useState({});
  useEffect(() => {
    const container = watchUpdateRef.current;
    Doms.onResize(container, async () => {
      for (let i = 0; i < 4; i++) {
        await Commons.sleep(i * 100);
        setState({});
      }
    });
    return () => Doms.offResize(container);
  }, [watchUpdateRef]);
  return [state, watchUpdateRef];
};

export const heightBoxWorldPhysical = (jaco?: { getBox(): Box3; matrixWorld: Matrix4 }): number => {
  return jaco ? jaco.getBox().applyMatrix4(jaco.matrixWorld).max.y : 0;
};

export const JSON_DECIMALS = 4;

export const filterOnlyIfResultNotEmpty =
  <T>(predicate: (item: T) => boolean): ((list: T[]) => T[]) =>
  (list: T[]): T[] => {
    const filtered = list.filter(predicate);
    if (filtered.length > 0) {
      return filtered;
    }
    return list;
  };

export const nextItem = <T>(array: T[], item: T): T =>
  array[(array.indexOf(item) + 1) % array.length];

export function getWorldAxesFromCameraDirection(): {
  zp: Vector3;
  xn: Vector3;
  zn: Vector3;
  xp: Vector3;
} {
  const cameraDirection = APP.camera.getWorldDirection(v3()).setY(0);
  const z = _.minBy([v3(1, 0, 0), v3(-1, 0, 0), v3(0, 0, 1), v3(0, 0, -1)], (d) =>
    d.angleTo(cameraDirection)
  ) as Vector3;
  const [zp, xn, zn, xp] = [0, 90, 180, 270].map((a) =>
    z.clone().applyAxisAngle(v3(0, 1, 0), (a * Math.PI) / 180)
  );
  return { zp, xn, zn, xp };
}

/**

 Wendet eine score-Funktion auf ein array an und gibt den Eintrag mit dem höchsten Score zurück

 falls der Score über 0 liegt

 @param array

 @param scoreFunction
 */
export function maxByIfGreaterZero<T>(array: T[], scoreFunction: (item: T) => number): T | null {
  const scores = array.map(scoreFunction);
  const maxScore = Math.max(...scores);
  if (maxScore <= 0) return null;
  return array[scores.indexOf(maxScore)];
}

export const union = <T>(lista: T[], listb: T[]): T[] => new Set([...lista, ...listb]).toArray();
